import { create, type StoreApi, useStore } from "zustand";
import { devtools, persist } from "zustand/middleware";
import {
  type CameraPermissionSlice,
  createCameraPermissionSlice,
} from "@/store/camera-permission-slice";
import { createEnvironmentSlice, type EnvironmentSlice } from "@/store/environment-slice";
import { createEnvironmentUrlSlice, type EnvironmentUrlSlice } from "@/store/environment-url-slice";
import { createIntentSlice, type IntentSlice } from "@/store/intent-slice";
import { createRealmSlice, type RealmSlice } from "@/store/realm-slice";
import { createHasHydratedSlice, type HasHydratedSlice } from "./hydration-slice";
import { createPendingAnswersSlice, type PendingAnswersSliceState } from "./pending-answers-slice";
import {
  createPendingPicturesSlice,
  type PendingPicturesSliceState,
} from "./pending-pictures-slice";
import {
  createPendingCameraEventsSlice,
  type PendingCameraEventsSliceState,
} from "./pending-camera-events-slice";

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

const createSelectors = <S extends StoreApi<object>>(_store: S) => {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {};
  for (const k of Object.keys(store.getState())) {
    (store.use as Record<string, () => unknown>)[k] = () =>
      useStore(_store, (s) => s[k as keyof typeof s]);
  }

  return store;
};

const useBoundStoreBase = create<
  CameraPermissionSlice &
    EnvironmentSlice &
    EnvironmentUrlSlice &
    IntentSlice &
    RealmSlice &
    HasHydratedSlice &
    PendingAnswersSliceState &
    PendingPicturesSliceState &
    PendingCameraEventsSliceState
>()(
  devtools(
    persist(
      (...a) => ({
        ...createCameraPermissionSlice(...a),
        ...createEnvironmentSlice(...a),
        ...createEnvironmentUrlSlice(...a),
        ...createIntentSlice(...a),
        ...createRealmSlice(...a),
        ...createHasHydratedSlice(...a),
        ...createPendingAnswersSlice(...a),
        ...createPendingPicturesSlice(...a),
        ...createPendingCameraEventsSlice(...a),
      }),
      {
        name: "persisted-bound-store",
        onRehydrateStorage: (state) => {
          return () => state.setHasHydrated(true);
        },
        partialize: (state) =>
          Object.fromEntries(
            Object.entries(state).filter(
              ([key]) => !["pendingAnswers", "pendingPictures"].includes(key)
            )
          ),
      }
    )
  )
);

const usePersistedBoundStore = createSelectors(useBoundStoreBase);

export default usePersistedBoundStore;
